<div class="w-full">
    <div class="p-4">
        <!-- Header -->
        <div class="flex justify-between items-center mb-4">
            <h2 class="text-2xl font-bold">Edit Venue</h2>
            <button mat-icon-button (click)="onCancel()" class="text-gray-500 hover:text-gray-700">
                <mat-icon>close</mat-icon>
            </button>
        </div>

        <form [formGroup]="venueForm" (ngSubmit)="onSubmit()">
            <!-- Basic Information -->
            <div class="mb-4">
                <h3 class="text-lg font-semibold mb-3 flex items-center">
                    <mat-icon class="mr-2">info</mat-icon>
                    Basic Information
                </h3>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <!-- Name -->
                    <mat-form-field class="w-full">
                        <mat-label>Venue Name</mat-label>
                        <input matInput formControlName="name" required>
                        <mat-error *ngIf="venueForm.get('name')?.hasError('required')">
                            Venue name is required
                        </mat-error>
                    </mat-form-field>

                    <!-- Status -->
                    <mat-form-field class="w-full">
                        <mat-label>Status</mat-label>
                        <mat-select formControlName="status" required>
                            <mat-option value="active">Active</mat-option>
                            <mat-option value="inactive">Inactive</mat-option>
                        </mat-select>
                        <mat-error *ngIf="venueForm.get('status')?.hasError('required')">
                            Status is required
                        </mat-error>
                    </mat-form-field>
                </div>
                
                <!-- Venue Status Controls -->
                <div class="mt-3 flex items-center gap-8">
                    <!-- Verification Status -->
                    <div class="flex items-center gap-3 whitespace-nowrap">
                        <div class="flex items-center gap-2">
                            <label class="text-sm font-medium text-gray-700">Verified Venue</label>
                            <mat-icon
                                matTooltip="Verified venues appear in the app search and are visible to users. Unverified venues are hidden from search results."
                                matTooltipPosition="above"
                                class="text-gray-400 text-base cursor-help"
                                style="font-size: 18px; width: 18px; height: 18px;">
                                info
                            </mat-icon>
                        </div>
                        <mat-slide-toggle
                            [(ngModel)]="verified"
                            [ngModelOptions]="{standalone: true}"
                            color="primary">
                        </mat-slide-toggle>
                    </div>

                    <!-- Onboarding Status -->
                    <div class="flex items-center gap-3 whitespace-nowrap">
                        <div class="flex items-center gap-2">
                            <label class="text-sm font-medium text-gray-700">Onboarded Venue</label>
                            <mat-icon
                                matTooltip="Onboarded venues have completed the setup process. Non-onboarded venues may have incomplete information."
                                matTooltipPosition="above"
                                class="text-gray-400 text-base cursor-help"
                                style="font-size: 18px; width: 18px; height: 18px;">
                                info
                            </mat-icon>
                        </div>
                        <mat-slide-toggle
                            [(ngModel)]="onBoarded"
                            [ngModelOptions]="{standalone: true}"
                            color="primary">
                        </mat-slide-toggle>
                    </div>

                    <!-- Featured Status -->
                    <div class="flex items-center gap-3 whitespace-nowrap">
                        <div class="flex items-center gap-2">
                            <label class="text-sm font-medium text-gray-700">Feature Venue</label>
                            <mat-icon
                                matTooltip="Featured venues are prioritized in customer venue listings and display a blue Featured badge in the app."
                                matTooltipPosition="above"
                                class="text-gray-400 text-base cursor-help"
                                style="font-size: 18px; width: 18px; height: 18px;">
                                info
                            </mat-icon>
                        </div>
                        <mat-slide-toggle
                            [(ngModel)]="featured"
                            [ngModelOptions]="{standalone: true}"
                            color="primary">
                        </mat-slide-toggle>
                    </div>
                </div>
            </div>

            <!-- Location -->
            <div class="mb-4">
                <h3 class="text-lg font-semibold mb-3 flex items-center">
                    <mat-icon class="mr-2">location_on</mat-icon>
                    Location
                </h3>
                <div class="grid grid-cols-1 gap-3">
                    <!-- Search Box for Google Maps -->
                    <mat-form-field class="w-full">
                        <mat-label>Search Location</mat-label>
                        <input #searchBox matInput placeholder="Type to search for a location..." [value]="venueForm.get('placeName')?.value">
                        <mat-icon matPrefix class="mr-2">search</mat-icon>
                        <mat-hint>Search for a location or click on the map to select</mat-hint>
                    </mat-form-field>

                    <!-- Google Map -->
                    <div class="w-full">
                        <google-map
                            height="300px"
                            width="100%"
                            [center]="center"
                            [zoom]="zoom"
                            [options]="mapOptions"
                            (mapClick)="onMapClick($event)"
                        >
                            <map-marker *ngIf="location.markerPos" [position]="location.markerPos"></map-marker>
                        </google-map>
                    </div>

                    <!-- Place Name -->
                    <mat-form-field class="w-full">
                        <mat-label>Place Name</mat-label>
                        <input matInput formControlName="placeName" required>
                        <mat-error *ngIf="venueForm.get('placeName')?.hasError('required')">
                            Place name is required
                        </mat-error>
                    </mat-form-field>

                    <!-- Place Address -->
                    <mat-form-field class="w-full">
                        <mat-label>Address</mat-label>
                        <textarea matInput formControlName="placeAddress" rows="2"></textarea>
                    </mat-form-field>
                </div>
            </div>

            <!-- Operating Hours -->
            <div class="mb-4">
                <h3 class="text-lg font-semibold mb-3 flex items-center">
                    <mat-icon class="mr-2">schedule</mat-icon>
                    Operating Hours
                </h3>
                <div class="bg-gray-50 rounded-lg p-4">
                    <div class="space-y-3">
                        <div *ngFor="let day of timing; let i = index" class="grid grid-cols-12 gap-3 items-center">
                            <div class="col-span-3">
                                <mat-checkbox [(ngModel)]="day.checked" [ngModelOptions]="{standalone: true}">
                                    {{ day.name }}
                                </mat-checkbox>
                            </div>
                            <div class="col-span-4">
                                <mat-form-field class="w-full">
                                    <mat-label>Start Time</mat-label>
                                    <mat-select [(ngModel)]="day.startTime" [ngModelOptions]="{standalone: true}" [disabled]="!day.checked">
                                        <mat-option *ngFor="let time of times.slice(0, -1)" [value]="time">
                                            {{ time }}
                                        </mat-option>
                                    </mat-select>
                                </mat-form-field>
                            </div>
                            <div class="col-span-4">
                                <mat-form-field class="w-full">
                                    <mat-label>End Time</mat-label>
                                    <mat-select [(ngModel)]="day.endTime" [ngModelOptions]="{standalone: true}" [disabled]="!day.checked">
                                        <mat-option *ngFor="let time of times.slice(1)" [value]="time" 
                                                    [disabled]="day.startTime && time <= day.startTime">
                                            {{ time }}
                                        </mat-option>
                                    </mat-select>
                                </mat-form-field>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Cancellation Policy -->
            <div class="mb-4">
                <h3 class="text-lg font-semibold mb-3 flex items-center">
                    <mat-icon class="mr-2">cancel</mat-icon>
                    Cancellation Policy
                </h3>
                <div class="bg-gray-50 rounded-lg p-4">
                    <div class="mb-4">
                        <mat-checkbox [(ngModel)]="cancellationPolicy.enabled" [ngModelOptions]="{standalone: true}">
                            Enable Cancellation Policy
                        </mat-checkbox>
                    </div>

                    <div *ngIf="cancellationPolicy.enabled" class="space-y-3">
                        <div *ngFor="let rule of cancellationPolicy.rules; let i = index" class="grid grid-cols-12 gap-3 items-center">
                            <div class="col-span-5">
                                <mat-form-field class="w-full">
                                    <mat-label>Hours Before Booking</mat-label>
                                    <input matInput type="number" 
                                           [(ngModel)]="rule.hoursBeforeBooking" 
                                           [ngModelOptions]="{standalone: true}"
                                           min="0">
                                </mat-form-field>
                            </div>
                            <div class="col-span-5">
                                <mat-form-field class="w-full">
                                    <mat-label>Refund Percentage</mat-label>
                                    <input matInput type="number" 
                                           [(ngModel)]="rule.refundPercentage" 
                                           [ngModelOptions]="{standalone: true}"
                                           min="0" max="100">
                                </mat-form-field>
                            </div>
                            <div class="col-span-2">
                                <button mat-icon-button type="button" (click)="removeCancellationRule(i)" color="warn">
                                    <mat-icon>delete</mat-icon>
                                </button>
                            </div>
                        </div>
                        <button mat-button type="button" (click)="addCancellationRule()" class="text-blue-600">
                            <mat-icon class="mr-1">add</mat-icon>
                            Add Rule
                        </button>
                    </div>
                </div>
            </div>

            <!-- Rules -->
            <div class="mb-4">
                <h3 class="text-lg font-semibold mb-3 flex items-center">
                    <mat-icon class="mr-2">gavel</mat-icon>
                    Rules
                </h3>
                <mat-form-field class="w-full">
                    <mat-label>Venue Rules</mat-label>
                    <textarea matInput formControlName="rules" rows="4"></textarea>
                </mat-form-field>
            </div>

            <!-- Privacy Policy -->
            <div class="mb-4">
                <h3 class="text-lg font-semibold mb-3 flex items-center">
                    <mat-icon class="mr-2">privacy_tip</mat-icon>
                    Privacy Policy
                </h3>
                <mat-form-field class="w-full">
                    <mat-label>Privacy Policy</mat-label>
                    <textarea matInput formControlName="privacyPolicy" rows="4"></textarea>
                </mat-form-field>
            </div>

            <!-- Actions -->
            <div class="flex justify-end gap-3 mt-4 pt-4 border-t">
                <button mat-button type="button" (click)="onCancel()" [disabled]="isLoading">
                    Cancel
                </button>
                <button mat-flat-button type="submit" color="primary" [disabled]="isLoading || venueForm.invalid">
                    <span *ngIf="!isLoading">Save Changes</span>
                    <span *ngIf="isLoading">Saving...</span>
                </button>
            </div>
        </form>
    </div>
</div>
